import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchMerchants, Merchant } from '../../services/merchant';
import { UserRole } from '../../types/auth';

export interface MerchantFilters {
  group: string;
  kycStatus: 'all' | 'Verified' | 'NotVerified' | 'Not Verified' | 'KycPending';
  dateRange: {
    from?: string;
    to?: string;
  };
}

export interface MerchantState {
  merchants: Merchant[];
  total: number;
  loading: boolean;

  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  filters: MerchantFilters;

  hasFetched: boolean;
  error?: string;
}

const initialState: MerchantState = {
  merchants: [],
  total: 0,
  loading: false,

  page: 1,
  pageSize: 10,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  searchQuery: '',
  filters: {
    group: '',
    kycStatus: 'all',
    dateRange: { from: undefined, to: undefined },
  },

  hasFetched: false,
};

// --- Async Thunk without generics ---
export const fetchMerchantsThunk = createAsyncThunk(
  'merchant/fetchMerchants',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Cast getState manually to your slice + auth
      const state = getState() as {
        merchant: MerchantState;
        auth: { token: string; user: any };
      };

      const { token, user } = state.auth;
      const { page, pageSize, sortBy, sortOrder, searchQuery, filters } = state.merchant;

      const isCleverUser = user?.role === UserRole.CLEVERPE_USER;
      const hasSearchOrFilters =
        searchQuery.trim() !== '' ||
        !!filters.group ||
        filters.kycStatus !== 'all' ||
        !!filters.dateRange?.from ||
        !!filters.dateRange?.to;

      if (isCleverUser && !hasSearchOrFilters) {
        return { merchants: [], total: 0, page, pageSize };
      }

      const res = await fetchMerchants({
        page,
        pageSize,
        sortField: sortBy,
        sortOrder,
        search: searchQuery,
        token,
        filters,
      });

      return {
        merchants: res.merchants,
        total: res.totalMatch,
        page: res.pageNo,
        pageSize: res.pageSize,
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// --- Slice ---
const merchantSlice = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilters: (state, action: PayloadAction<MerchantFilters>) => {
      state.filters = action.payload;
      state.page = 1;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.page = 1;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMerchantsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchMerchantsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.merchants = action.payload.merchants;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.hasFetched = true;
      })
      .addCase(fetchMerchantsThunk.rejected, (state, action) => {
        state.loading = false;
        state.merchants = [];
        state.total = 0;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  setFilters,
  resetFilters,
  setHasFetched,
} = merchantSlice.actions;

export default merchantSlice.reducer;
